import {Injectable, signal} from '@angular/core';
import {ProcessStateEnum, RepositoryProcessPayload} from '../types-repository';
import {ProcessService} from './process.service';

/** An editing workspace this browser asked for, which may not have a volume yet. */
export interface PendingWorkspace {
  processId: string;
  /** URN of the organization it was created in: the source-folder links need it (never the pool). */
  organizationId: string;
  pool: string;
  mountPoint: string;
  contents: string[];
  availabilityZone: string;
  /** Epoch milliseconds. */
  startedAt: number;
}

const STORAGE_KEY = 'pending_editing_workspaces';
const CLAIMED_KEY = 'pending_editing_workspaces_claimed';
// A Bulk restore takes up to ~12 h and staging a large workspace hours more; anything older is stale.
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * Editing workspaces that are still being created. Creating one can take hours (content archived in
 * Glacier is restored first) and there is no volume to list until then, so the browser that started it
 * remembers the process (localStorage, surviving reloads) and follows it through ProcessService, whose
 * message says what the workspace is doing. Only this browser sees these rows.
 */
@Injectable({providedIn: 'root'})
export class PendingWorkspacesService {
  readonly entries = signal<PendingWorkspace[]>([]);
  /** The latest known process per processId. */
  readonly processes = signal<Record<string, RepositoryProcessPayload>>({});
  /**
   * Volume IDs of finished creations whose entries are gone: no older entry still running may take them. Kept as
   * long as any entry is, and with them across reloads -- the older entry outlives the finished one.
   */
  readonly claimedVolumes = signal<string[]>([]);

  constructor(private processService: ProcessService) {
    const entries = this.load().filter(e => Date.now() - e.startedAt < MAX_AGE_MS);
    this.entries.set(entries);
    this.save(entries);
    this.claimedVolumes.set(entries.length ? this.loadClaimed() : []);
    this.saveClaimed(this.claimedVolumes());
    entries.forEach(e => this.watch({
      __typename: 'RepositoryProcessPayload',
      id: e.processId,
      state: ProcessStateEnum.InProgress,
      message: null,
      data: null,
      action: 'createEditingWorkspace',
    } as RepositoryProcessPayload));
  }

  add(entry: PendingWorkspace, process: RepositoryProcessPayload) {
    const entries = [...this.entries().filter(e => e.processId !== entry.processId), entry];
    this.entries.set(entries);
    this.save(entries);
    this.watch(process);
  }

  /** Remember volumes finished creations made, see `claimedVolumes`. */
  claim(volumeIds: string[]) {
    const fresh = volumeIds.filter(v => !this.claimedVolumes().includes(v));
    if (fresh.length) {
      this.claimedVolumes.set([...this.claimedVolumes(), ...fresh]);
      this.saveClaimed(this.claimedVolumes());
    }
  }

  remove(processId: string) {
    const entries = this.entries().filter(e => e.processId !== processId);
    this.entries.set(entries);
    this.save(entries);
    if (!entries.length && this.claimedVolumes().length) {
      // Nothing left that could take a volume.
      this.claimedVolumes.set([]);
      this.saveClaimed([]);
    }
    const {[processId]: _, ...rest} = this.processes();
    this.processes.set(rest);
  }

  private watch(process: RepositoryProcessPayload) {
    this.processes.update(ps => ({...ps, [process.id]: process}));
    this.processService.observeProcess(process).subscribe(p => {
      // Dismissed or dropped meanwhile: a late poll must not bring it back.
      if (this.entries().some(e => e.processId === p.id)) {
        this.processes.update(ps => ({...ps, [p.id]: p as RepositoryProcessPayload}));
      }
    });
  }

  private load(): PendingWorkspace[] {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  private loadClaimed(): string[] {
    try {
      return JSON.parse(localStorage.getItem(CLAIMED_KEY) || '[]');
    } catch {
      return [];
    }
  }

  private saveClaimed(volumeIds: string[]) {
    try {
      localStorage.setItem(CLAIMED_KEY, JSON.stringify(volumeIds));
    } catch {
      // Storage unavailable: the claims last as long as the entries in memory do.
    }
  }

  private save(entries: PendingWorkspace[]) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // Storage unavailable (private mode, quota): the rows then last until the page is closed.
    }
  }
}
